<template>
  <div>
    <v-container>
      <v-row cols="12" sm="12">
        <div class="pa-5">
          <div class="headline text-uppercase">Public Forms</div>
          <div class="subtitle-1">Query list of public forms</div>
        </div>
      </v-row>

      <v-row justify="start" class="mx-5 pt-4">
        <v-col cols="12" sm="4" class="py-0">
          <v-select
            dense
            :items="forms"
            placeholder="Forms"
            item-text="title"
            item-value="id"
            v-model="selectedFormId"
            @change="changeForm"
          />
        </v-col>
      </v-row>
      <v-row class="mx-5">
        <v-col class="pb-0">
          <v-text-field
            dense
            label="Search"
            :append-icon="
              search === null || search.length === 0 ? 'search' : ''
            "
            clearable
            color="primary"
            autocomplete="off"
            @input="searchForms"
            v-model="search"
          />
        </v-col>
      </v-row>

      <div v-if="allPublicSubmissions.length">
        <v-row
          justify="center"
          v-for="submission in allPublicSubmissions"
          :key="submission.id"
          class="mx-4"
        >
          <v-col cols="12" md="12">
            <v-hover v-slot:default="{ hover }">
              <v-card
                @click="openForm(submission)"
                class="mx-auto pointed-cursor"
                outlined
                ripple
                :elevation="hover ? 2 : 0"
              >
                <v-row>
                  <v-col
                    v-for="data in submission.data"
                    :key="data.option_key"
                    md="2"
                  >
                    <v-card-title class="pr-0 text-break">{{
                      data.name
                    }}</v-card-title>
                    <v-card-text class="pr-0 text-break">{{
                      data.data || 'N/A'
                    }}</v-card-text>
                  </v-col>
                  <v-col
                    style="width: 4% !important"
                    class="pt-12 pr-8 pl-4 text-end"
                    v-if="deletePermission || submission.pdf_url"
                  >
                    <v-tooltip top v-if="submission.pdf_url">
                      <template #activator="{ on }">
                        <v-btn
                          color="black"
                          dark
                          class="mr-1"
                          fab
                          x-small
                          depressed
                          v-on="on"
                          @click.stop.prevent="generatePdf(submission.pdf_url)"
                        >
                          <v-icon>picture_as_pdf</v-icon>
                        </v-btn>
                      </template>
                      <span>PDF</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template #activator="{ on }">
                        <v-btn
                          color="error"
                          fab
                          x-small
                          depressed
                          v-on="on"
                          @click.stop.prevent="openDeleteModal(submission)"
                        >
                          <v-icon>delete</v-icon>
                        </v-btn>
                      </template>
                      <span>Delete this submission</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
        <div v-intersect="onIntersect"></div>
      </div>
      <div v-else>
        <v-row justify="center" class="mx-4">
          <v-col cols="12" md="12">
            <div class="text-center title">No submission found.</div>
          </v-col>
        </v-row>
      </div>
    </v-container>

    <v-dialog v-model="deleteModal" :width="300">
      <v-card>
        <v-card-title class="mx-0 pb-0">Delete submission?</v-card-title>
        <v-card-subtitle class="mx-0 mt-1">Are you sure?</v-card-subtitle>
        <v-card-actions class="text-center">
          <v-spacer></v-spacer>
          <v-btn text class="mr-1 lighten-1" @click="deleteModal = false"
            >Cancel</v-btn
          >
          <v-btn text class="mr-1 lighten-1" @click="deleteSubmission"
            >Ok</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import uiHelper from '@/helpers/ui'
const _debounce = require('lodash.debounce')

export default {
  name: 'PublicSubmissions',

  data() {
    return {
      selectedFormId: '',
      startDate: '',
      endDate: '',
      search: '',
      totalPageCount: 0,
      pageNumber: 1,
      deleteModal: false,
      selectedSubmission: {}
    }
  },

  computed: {
    ...mapGetters({
      forms: 'user/publicForms',
      permissions: 'user/userPermissions',
      allSubmissions: 'form/allSubmissions',
      selectedForm: 'form/selectedForm'
    }),

    allPublicSubmissions() {
      if (this.allSubmissions.length) {
        return this.allSubmissions
      }
      return []
    },
    deletePermission() {
      if (this.permissions?.app_permissions?.submissions) {
        return this.permissions.app_permissions.submissions.delete
      }
      return false
    }
  },

  async mounted() {
    uiHelper.progressBarStatus(true)

    this.clearAllSubmissions()
    let response = await this.getMyForms(true)
    if (!response) {
      uiHelper.progressBarStatus(false)
    }
    let selectedForm =
      this.selectedForm && this.selectedForm.public
        ? this.selectedForm
        : this.forms[0]
    this.selectedFormId = selectedForm.id
    this.setSelectedForm(selectedForm)
    this.setSelectedFormId(selectedForm.id)
    let _response = await this.getPublicSubmissions({
      formId: this.selectedFormId
    })
    if (_response) {
      this.totalPageCount = _response?.data?.payload?.total_pages
      this.pageNumber = _response?.data?.payload?.page
    }
    uiHelper.progressBarStatus(false)
  },

  watch: {
    search: {
      handler(val) {
        if (val === null) {
          this.search = ''
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions({
      getPublicSubmissions: 'form/getPublicSubmissions',
      // generatePDF: 'form/generatePDF',
      getMyForms: 'form/getMyForms',
      cancelSubmission: 'form/cancelSubmission'
    }),

    ...mapMutations({
      setSelectedSubmissionToken: 'form/setSelectedSubmissionToken',
      setSelectedSubmissionDetails: 'form/setSelectedSubmissionDetails',
      setSelectedSubmission: 'form/setSelectedSubmission',
      setSelectedForm: 'form/setSelectedForm',
      setSelectedFormId: 'form/setSelectedFormId',
      setAllSubmissions: 'form/setAllSubmissions',
      clearAllSubmissions: 'form/clearAllSubmissions'
    }),

    async changeForm(formId) {
      uiHelper.progressBarStatus(true)
      this.search = ''
      let selectedForm = this.forms.find(el => el.id === formId)
      this.setSelectedFormId(formId)
      this.setSelectedForm(selectedForm)
      let response = await this.getPublicSubmissions({
        formId: this.selectedFormId,
        searchTerm: this.search
      })
      if (response) {
        this.pageNumber = response?.data?.payload?.page
        this.totalPageCount = response?.data?.payload?.total_pages
      }
      uiHelper.progressBarStatus(false)
    },

    async openForm(submission) {
      this.setSelectedSubmissionToken(submission.submission_token)
      this.setSelectedSubmissionDetails(submission)
      this.setSelectedSubmission(submission)
      this.$router.push({
        name: 'Submission',
        params: { id: submission.submission_token }
      })
    },

    openDeleteModal(submission) {
      this.selectedSubmission = submission
      this.setSelectedSubmissionToken(submission.submission_token)
      this.deleteModal = true
    },

    async deleteSubmission() {
      const submission = this.selectedSubmission
      this.setSelectedSubmissionToken(submission.submission_token)
      uiHelper.progressBarStatus(true)

      let response
      try {
        response = await this.cancelSubmission(this.selectedSubmission.form_id)

        let index = this.allSubmissions.findIndex(el => el.id === submission.id)

        let allSubmissions = [...this.allSubmissions]
        allSubmissions.splice(index, 1)
        this.setAllSubmissions(allSubmissions)

        if (response) {
          uiHelper.showSnackbarMessage('Successfully deleted submission')
        }
      } catch (error) {
        uiHelper.showSnackbarMessage(error.response.data.msg, 'error')
      } finally {
        this.deleteModal = false
        this.setSelectedSubmissionToken('')
        uiHelper.progressBarStatus(false)
      }
    },

    // async exportPDF(submission) {
    //   await this.generatePDF(submission.submission_token)
    // },

    searchForms: _debounce(async function() {
      if (!this.selectedFormId) {
        uiHelper.showSnackbarMessage('No form selected', 'error')
        return
      }
      uiHelper.progressBarStatus(true)
      try {
        let response = await this.getPublicSubmissions({
          formId: this.selectedFormId,
          searchTerm: this.search
        })
        this.pageNumber = response?.data?.payload?.page
        this.totalPageCount = response?.data?.payload?.total_pages
      } catch (error) {
        uiHelper.progressBarStatus(false)
        throw error
      }
      uiHelper.progressBarStatus(false)
    }, 1000),

    onIntersect(entries, observer, isIntersecting) {
      if (isIntersecting === true) {
        if (this.totalPageCount === 0 || this.totalPageCount === 1) return
        else if (this.pageNumber < this.totalPageCount) {
          uiHelper.progressBarStatus(true)

          let pageNumber = this.pageNumber

          this.pageNumber = pageNumber + 1
          this.getPublicSubmissions({
            formId: this.selectedFormId,
            searchTerm: this.search,
            page: this.pageNumber
          }).then(response => {
            this.totalPageCount = response?.data?.payload?.total_pages
            this.pageNumber = response?.data?.payload?.page
          })
          uiHelper.progressBarStatus(false)
        }
      }
    },
    generatePdf(url) {
      if (!url)
        return uiHelper.showSnackbarMessage('something went wrong', 'error')
      window.open(url, '_blank')
    }
  }
}
</script>
